import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchPerformers,
  fetchPerformer,
  updatePerformer,
  moveToNotes,
  type UpdatePerformerInput,
} from '@/services/api/performers'
import { dataKeys } from './useDashboardData'

export const performerKeys = {
  all: ['performers'] as const,
  lists: () => [...performerKeys.all, 'list'] as const,
  list: () => [...performerKeys.lists()] as const,
  details: () => [...performerKeys.all, 'detail'] as const,
  detail: (id: number) => [...performerKeys.details(), id] as const,
}

export function usePerformers() {
  return useQuery({
    queryKey: performerKeys.list(),
    queryFn: fetchPerformers,
  })
}

export function usePerformer(id: number) {
  return useQuery({
    queryKey: performerKeys.detail(id),
    queryFn: () => fetchPerformer(id),
    enabled: id > 0,
  })
}

export function useUpdatePerformer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdatePerformerInput }) =>
      updatePerformer(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: performerKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: performerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}

export function useMoveToNotes() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: number }) => moveToNotes(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}
